import { useState, useEffect, useCallback, useMemo } from "react";
import { ReservationService } from "../services/ReservationService";
import { AppVariablesService } from "../services/AppVariablesService";
import Logo from "../components/Logo";
import BackButton from "../components/BackButton";
import {
  formatDateLabel,
  formatReservationDateTime,
} from "../helpers/formatDateLabel";

export default function Checkin() {
  const service = useMemo(() => new ReservationService(), []);
  const appVariablesService = useMemo(() => new AppVariablesService(), []);

  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [alwaysShowCheckinButton, setAlwaysShowCheckinButton] = useState(false);
  const [requireLocationForCheckin, setRequireLocationForCheckin] =
    useState(false);
  const [processingCheckin, setProcessingCheckin] = useState(false);

  const fetchReservations = useCallback(async () => {
    try {
      const data = await service.getReservations();
      // Get reservations that start OR end today and haven't been fully checked out
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const todayReservations =
        data?.items?.filter((reservation) => {
          // For overnight reservations, use startHour as the actual start date
          // For regular reservations, use date field
          const startDateTime = reservation.overnight && reservation.startHour 
            ? new Date(reservation.startHour)
            : new Date(reservation.date);
          
          const reservationStartDate = new Date(startDateTime);
          reservationStartDate.setHours(0, 0, 0, 0);
          
          const startsToday =
            reservationStartDate.getTime() === today.getTime();

          // Check if reservation ends today (handle both endHour and endDate)
          let endsToday = false;
          if (reservation.endHour || reservation.endDate) {
            const endDateTime = reservation.endDate ? reservation.endDate : reservation.endHour;
            const reservationEndDate = new Date(endDateTime);
            reservationEndDate.setHours(0, 0, 0, 0);
            endsToday = reservationEndDate.getTime() === today.getTime();
          }

          // Check if today falls within a multi-day reservation span
          let spansToday = false;
          if (reservation.overnight && (reservation.endHour || reservation.endDate)) {
            const endDateTime = reservation.endDate ? reservation.endDate : reservation.endHour;
            const reservationEndDate = new Date(endDateTime);
            reservationEndDate.setHours(0, 0, 0, 0);
            
            // Today is between start and end dates (exclusive of boundaries)
            spansToday = today.getTime() > reservationStartDate.getTime() && 
                         today.getTime() < reservationEndDate.getTime();
            
            // Debug logging for overnight reservations
            if (reservation.overnight) {
              console.log('Overnight reservation check:', {
                id: reservation._id,
                date: reservation.date,
                startHour: reservation.startHour,
                endHour: reservation.endHour,
                actualStartDate: reservationStartDate.toISOString(),
                actualEndDate: reservationEndDate.toISOString(),
                today: today.toISOString(),
                startsToday,
                endsToday,
                spansToday,
                hasCheckout: !!reservation.checkout
              });
            }
          }

          // Include reservation if it starts today, ends today, or spans across today, and hasn't been fully checked out
          return (startsToday || endsToday || spansToday) && !reservation.checkout;
        }) || [];

      setReservations(todayReservations);
      setError(null);
    } catch (err) {
      console.error("Erro ao buscar reservas:", err);
      setError("Nenhuma reserva encontrada.");
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  useEffect(() => {
    // Fetch app variables to get the alwaysShowCheckinButton setting
    const fetchAppVariables = async () => {
      try {
        const response = await appVariablesService.find();
        if (response?.config?.alwaysShowCheckinButton) {
          console.log(
            "alwaysShowCheckinButton",
            response.config.alwaysShowCheckinButton
          );
          setAlwaysShowCheckinButton(response.config.alwaysShowCheckinButton);
        }
        if (response?.config?.requireLocationForCheckin !== undefined) {
          console.log(
            "requireLocationForCheckin",
            response.config.requireLocationForCheckin
          );
          setRequireLocationForCheckin(
            response.config.requireLocationForCheckin
          );
        }
      } catch (err) {
        console.error("Error fetching app variables:", err);
      }
    };
    fetchAppVariables();
  }, [appVariablesService]);

  const handleCheckin = async (reservationId) => {
    console.log("handleCheckin", reservationId);
    setProcessingCheckin(true);

    const getCurrentPositionWithTimeout = (timeout = 30000) =>
      new Promise((resolve, reject) => {
        const timer = setTimeout(
          () => reject(new Error("Timeout na geolocalização")),
          timeout
        );

        const options = {
          enableHighAccuracy: true,
          timeout: timeout,
          maximumAge: 0,
        };

        navigator.geolocation.getCurrentPosition(
          (pos) => {
            clearTimeout(timer);
            console.log("Localização obtida:", pos);
            resolve(pos);
          },
          (err) => {
            clearTimeout(timer);
            console.error("Erro ao obter localização:", err);
            // Don't show alert, just reject the promise
            reject(err);
          },
          options
        );
      });

    let coordinates = null;

    try {
      // Try to get location
      const position = await getCurrentPositionWithTimeout();
      coordinates = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
      console.log("Enviando coordenadas:", coordinates);
    } catch (locationError) {
      // If location is required and we couldn't get it, show error and stop
      if (requireLocationForCheckin) {
        console.error("Localização obrigatória não disponível:", locationError);
        setErrorMessage(
          locationError?.message === "User denied Geolocation"
            ? "Permissão de localização negada. A localização é obrigatória para fazer check-in."
            : "Erro ao obter localização. A localização é obrigatória para fazer check-in."
        );
        setProcessingCheckin(false);
        return;
      }

      // Location failed, but it's not required, so continue with checkin without coordinates
      console.warn(
        "Localização não disponível, continuando sem coordenadas:",
        locationError
      );
      coordinates = null;
    }

    try {
      // Send checkin with or without coordinates
      console.log("Sending checkin with coordinates:", coordinates);
      const response = await service.checkin(reservationId, coordinates);
      console.log("Checkin response:", response);

      if (response && !response.error) {
        setSuccessMessage("Check-in realizado com sucesso!");
        setErrorMessage(null);
        setProcessingCheckin(false);

        // Add delay before refreshing the reservations list
        // This keeps the reservation visible so users can see the success message
        setTimeout(async () => {
          await fetchReservations();
        }, 5000);
      } else {
        console.log("err", response);
        setErrorMessage("Erro ao fazer check-in.");
        setProcessingCheckin(false);
      }
    } catch (error) {
      console.error("Erro ao fazer check-in:", error);
      setErrorMessage(error?.message || "Erro ao fazer check-in.");
      setProcessingCheckin(false);
    }
  };

  const handleCheckout = async (reservationId) => {
    try {
      const response = await service.checkout(reservationId);
      if (!response.error) {
        setSuccessMessage("Check-out realizado com sucesso!");
        setErrorMessage(null);

        // Add 5-second delay before refreshing the reservations list
        // This keeps the reservation visible so users can see the success message
        setTimeout(async () => {
          await fetchReservations();
        }, 5000);
      } else {
        setErrorMessage("Erro ao fazer check-out.");
      }
    } catch (error) {
      console.error("Erro ao fazer check-out:", error);
      setErrorMessage(error.message || "Erro ao fazer check-out.");
    }
  };

  const canCheckin = (reservation) => {
    // If the rule is enabled, always show the checkin button
    if (alwaysShowCheckinButton) {
      return true;
    }

    // Otherwise, use the time-based logic
    if (!reservation.startHour && !reservation.startDate) return false;

    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let reservationStartTime;
    
    // Check if we have startDate (ISO datetime string from API) or just startHour (HH:mm string)
    if (reservation.startDate) {
      // For reservations from the API that include full datetime
      reservationStartTime = new Date(reservation.startDate);
    } else if (reservation.startHour && typeof reservation.startHour === 'string' && reservation.startHour.includes(':')) {
      // Legacy format with just time string HH:mm
      const [hour, minute] = reservation.startHour.split(":");
      reservationStartTime = new Date(reservation.date);
      reservationStartTime.setHours(parseInt(hour), parseInt(minute), 0, 0);
    } else if (reservation.startHour) {
      // startHour might be a full Date object/string
      reservationStartTime = new Date(reservation.startHour);
    } else {
      return false;
    }

    // Check if this is an overnight/multi-day reservation
    let isOvernight = reservation.overnight || false;
    if (!isOvernight && (reservation.endDate || reservation.endHour)) {
      const endTime = reservation.endDate ? new Date(reservation.endDate) : new Date(reservation.endHour);
      const startDate = new Date(reservationStartTime);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(endTime);
      endDate.setHours(0, 0, 0, 0);
      isOvernight = endDate.getTime() > startDate.getTime();
    }
    
    // For multi-day reservations that span today, always allow checkin
    if (isOvernight && (reservation.endDate || reservation.endHour)) {
      const endTime = reservation.endDate ? new Date(reservation.endDate) : new Date(reservation.endHour);
      const startDate = new Date(reservationStartTime);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(endTime);
      endDate.setHours(0, 0, 0, 0);
      
      // If today is between start and end dates (inclusive), allow checkin
      if (today >= startDate && today <= endDate) {
        return true;
      }
    }
    
    // Regular time window check for same-day reservations
    const oneHourBefore = new Date(reservationStartTime.getTime() - 60 * 60 * 1000);
    
    // If it's an overnight reservation, extend the window to the end time + 8 hours
    let windowEnd;
    if (isOvernight && (reservation.endDate || reservation.endHour)) {
      const endTime = reservation.endDate ? new Date(reservation.endDate) : new Date(reservation.endHour);
      windowEnd = new Date(endTime.getTime() + 8 * 60 * 60 * 1000);
    } else {
      // Regular reservation: 8 hours after start time
      windowEnd = new Date(reservationStartTime.getTime() + 8 * 60 * 60 * 1000);
    }

    return now >= oneHourBefore && now <= windowEnd;
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <h2>Carregando reservas...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-5">
        <BackButton />
        <h2>{error}</h2>
      </div>
    );
  }

  if (reservations.length === 0) {
    return (
      <>
        <div
          className="d-flex align-items-center justify-content-center position-relative"
          style={{ height: "125px" }}
        >
          <BackButton />
          <Logo center className="mb-4" size="125px" />
        </div>

        <div className="text-center p-5">
          <h2>Nenhuma reserva encontrada.</h2>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Loading Modal */}
      {processingCheckin && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 9999,
          }}
        >
          <div className="text-center text-white">
            <div
              className="spinner-border text-light mb-3"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            >
              <span className="visually-hidden">Carregando...</span>
            </div>
            <h4>Processando...</h4>
            <p className="text-light text-white">Por favor, aguarde</p>
          </div>
        </div>
      )}

      <div className="container p-5">
        <div
          className="d-flex align-items-center justify-content-center position-relative"
          style={{ height: "125px" }}
        >
          <BackButton />
          <Logo center className="mb-4" size="125px" />
        </div>

        <div>
          <h1 className="text-center text-uppercase fw-bold mb-5">
            Fazer Checkin
          </h1>
        </div>

        {/* Success alert */}
        {successMessage && (
          <div className="alert alert-success text-center" role="alert">
            {successMessage}
          </div>
        )}

        {/* Error alert */}
        {errorMessage && (
          <div className="alert alert-danger text-center" role="alert">
            {errorMessage}
          </div>
        )}

        {reservations.map((reservation, index) => (
          <div className="row" key={reservation._id || index}>
            <div className="col-12 mb-4 text-center">
              <div className="mb-2">
                <div className="fw-bold text-uppercase fs-2 mb-2 mt-5">
                  {/* For overnight reservations, use startHour to show the date */}
                  {reservation.overnight && reservation.startHour 
                    ? formatDateLabel(reservation.startHour, reservation.type)
                    : formatDateLabel(reservation.date, reservation.type)}
                </div>
                <div>
                  {/* Show start time */}
                  {reservation.startDate 
                    ? formatReservationDateTime(reservation.startDate)
                    : formatReservationDateTime(reservation.startHour)}
                  {/* Show end time if it's an overnight reservation */}
                  {(reservation.endDate || reservation.endHour) && (() => {
                    const endDateTime = reservation.endDate || reservation.endHour;
                    let isOvernight = reservation.overnight || false;
                    if (!isOvernight) {
                      const startDateField = reservation.overnight && reservation.startHour 
                        ? reservation.startHour 
                        : reservation.date;
                      const startDate = new Date(startDateField);
                      startDate.setHours(0, 0, 0, 0);
                      const endDate = new Date(endDateTime);
                      endDate.setHours(0, 0, 0, 0);
                      isOvernight = endDate.getTime() > startDate.getTime();
                    }
                    
                    if (isOvernight) {
                      return ` até ${formatReservationDateTime(endDateTime)}`;
                    }
                    return null;
                  })()}
                </div>
              </div>
              <div>
                <div>{reservation.school?.name || "Escola não encontrada"}</div>
                <div>
                  {reservation.school?.address?.neighborhood || "Sem endereço"}
                </div>
              </div>
            </div>

            {/* Check-in/Check-out buttons */}
            {(!reservation.checkin || !reservation.checkout) && (
              <div className="text-center">
                {/* Botão de Check-in: só aparece se não tiver checkin e se estiver no horário permitido */}
                {!reservation.checkin && canCheckin(reservation) && (
                  <button
                    className="btn btn-primary ms-2 text-uppercase btn-lg mt-5"
                    onClick={() => handleCheckin(reservation._id)}
                  >
                    Fazer Checkin
                  </button>
                )}

                {/* Botão de Check-out: aparece se já fez checkin e não fez checkout */}
                {/* Quando a regra 8 está habilitada, sempre mostra o botão de checkout */}
                {(alwaysShowCheckinButton || reservation.checkin) &&
                  !reservation.checkout && (
                    <button
                      className="btn btn-danger ms-2 text-uppercase btn-lg mt-5"
                      onClick={() => handleCheckout(reservation._id)}
                    >
                      Fazer Checkout
                    </button>
                  )}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
